import CloseLineIcon from '@ben/icons/close-line';
import IconButton from '@ben/orion/icon-button';
import Input, { InputVariant } from '@ben/orion/input';
import Select from '@ben/orion/select';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { isNil } from 'lodash';
import { useEffect, useRef, type ChangeEvent } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { acceptedContactTypesOptions } from '../modular-components/_common/acceptedContactTypesOptions';
import {
  FormContainer,
  FormField,
  FormHeader,
  FormRow,
  Spacer,
  StyledTypography,
} from './styles';
import type { RestrictionFormPropsWithTrigger } from './types';

interface ConditionalContactAgeRestrictionItem {
  contactType?: string;
  priority?: number | string;
}

const ConditionalContactAgeRestrictionForm = ({
  index,
  control,
  restrictionSetPath,
  onRemove,
  trigger,
}: RestrictionFormPropsWithTrigger) => {
  const hasMountedRef = useRef(false);
  const basePath = `${restrictionSetPath}.conditionalContactAgeRestrictions.${index}`;
  const restrictionsPath = `${restrictionSetPath}.conditionalContactAgeRestrictions`;
  const contactTypeFieldName = `${basePath}.contactType`;
  const priorityFieldName = `${basePath}.priority`;
  const minAgeFieldName = `${basePath}.minAge`;
  const maxAgeFieldName = `${basePath}.maxAge`;

  const contactType = useWatch({
    control,
    name: `${basePath}.contactType`,
  }) as string | undefined;

  const minAge = useWatch({
    control,
    name: `${basePath}.minAge`,
  }) as number | string | undefined;

  const maxAge = useWatch({
    control,
    name: `${basePath}.maxAge`,
  }) as number | string | undefined;

  const restrictions =
    (useWatch({
      control,
      name: restrictionsPath,
    }) as ConditionalContactAgeRestrictionItem[] | undefined) ?? [];

  const validateMinAge = (value: number | string | null | undefined) => {
    if (isNil(value) || value === '') {
      return true;
    }

    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      return 'Min age must be zero or greater';
    }

    if (isNil(maxAge) || maxAge === '') {
      return true;
    }

    return parsedValue <= Number(maxAge)
      ? true
      : 'Min age must be less than or equal to max age';
  };

  const validateMaxAge = (value: number | string | null | undefined) => {
    if (isNil(value) || value === '') {
      return true;
    }

    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      return 'Max age must be zero or greater';
    }

    if (isNil(minAge) || minAge === '') {
      return true;
    }

    return parsedValue >= Number(minAge)
      ? true
      : 'Max age must be greater than or equal to min age';
  };

  const validatePriority = (value: number | string | null | undefined) => {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      return 'Priority must be zero or greater';
    }

    const hasDuplicate = restrictions.some((restriction, restrictionIndex) => {
      return (
        restrictionIndex !== index &&
        restriction.contactType === contactType &&
        Number(restriction.priority) === parsedValue
      );
    });

    return hasDuplicate ? 'Priority must be unique per contact type' : true;
  };

  useEffect(
    function revalidatePriorityWhenRestrictionsChange() {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }

      void trigger(priorityFieldName);
    },
    [restrictions, trigger, priorityFieldName]
  );

  return (
    <FormContainer>
      <FormHeader>
        <Typography variant={TypographyVariant.bodyMd1}>
          Conditional Contact Age Restriction: #{index + 1}
        </Typography>
        <Spacer />
        <IconButton
          onClick={onRemove}
          aria-label="Remove restriction"
          icon={CloseLineIcon}
          size="sm"
          variant="action"
        />
      </FormHeader>

      <FormRow>
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          Contact type
        </StyledTypography>
        <Controller
          control={control}
          name={contactTypeFieldName}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              data={acceptedContactTypesOptions.map((option) => ({
                value: option.key,
                label: option.label,
              }))}
              width="220px"
              onChange={(value) => {
                field.onChange(value);
                void trigger(priorityFieldName);
              }}
            />
          )}
        />
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          Priority
        </StyledTypography>
        <Controller
          control={control}
          name={priorityFieldName}
          rules={{ required: true, validate: validatePriority }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Priority"
              type="number"
              showLabel={false}
              width="120px"
              min={0}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </FormRow>

      <FormRow>
        <Controller
          control={control}
          name={minAgeFieldName}
          rules={{ validate: validateMinAge }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Min age (optional)"
              type="number"
              width="100%"
              min={0}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                field.onChange(event);
                void trigger(maxAgeFieldName);
              }}
            />
          )}
        />

        <Controller
          control={control}
          name={maxAgeFieldName}
          rules={{ validate: validateMaxAge }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Max age (optional)"
              type="number"
              width="100%"
              min={0}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                field.onChange(event);
                void trigger(minAgeFieldName);
              }}
            />
          )}
        />
      </FormRow>

      <FormField>
        <Controller
          control={control}
          name={`${basePath}.condition`}
          rules={{ required: true, maxLength: 3000 }}
          render={({ field }) => (
            <Input
              {...field}
              name="Condition formula"
              showLabel
              variant={InputVariant.TextArea}
              maxLength={3000}
              width="100%"
            />
          )}
        />
      </FormField>
    </FormContainer>
  );
};

export default ConditionalContactAgeRestrictionForm;
